import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  X,
  Share2,
  Users,
  Gift,
  Trophy,
  Star,
  Copy,
  MessageCircle,
  Check,
  ArrowRight,
  Sparkles,
  Crown,
  TrendingUp,
  Zap
} from "lucide-react";

const ReferralOnboarding = ({ onComplete, autoPlay = false, showCloseButton = true }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [hasInteracted, setHasInteracted] = useState(false);

  const slides = [
    {
      id: "welcome",
      title: "Welcome to the Referral Program",
      subtitle: "Earn rewards by growing our community",
      bg: "from-amber-500/20 via-stone-900 to-stone-900",
      content: (
        <div className="text-center space-y-6">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-amber-500/30">
            <Trophy className="w-12 h-12 text-black" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">Share & Earn</h3>
            <p className="text-stone-400 max-w-md mx-auto">
              Every friend you bring to MyHairChair earns you loyalty points, 
              exclusive badges, and can even win you FREE Pro membership!
            </p>
          </div>
          <div className="flex justify-center gap-8 pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-400">100+</p>
              <p className="text-stone-500 text-sm">Points/Referral</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-400">5</p>
              <p className="text-stone-500 text-sm">Reward Tiers</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-400">FREE</p>
              <p className="text-stone-500 text-sm">Pro Membership</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "step1",
      title: "Step 1: Get Your Unique Link",
      subtitle: "Your personal referral link is ready",
      bg: "from-blue-500/20 via-stone-900 to-stone-900",
      content: (
        <div className="space-y-6">
          {/* Mock Dashboard Screenshot */}
          <div className="bg-stone-800 rounded-xl p-4 border border-stone-700 max-w-lg mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-stone-500 text-xs ml-2">Referral Dashboard</span>
            </div>
            <div className="space-y-3">
              <p className="text-stone-400 text-sm">Your Referral Link</p>
              <div className="flex gap-2">
                <div className="flex-1 bg-stone-900 rounded-lg px-3 py-2 text-amber-400 text-sm font-mono truncate">
                  myhairchair.com/register?ref=abc123
                </div>
                <div className="bg-amber-500 rounded-lg px-3 py-2 text-black">
                  <Copy className="w-4 h-4" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <div className="flex-1 bg-green-600 rounded-lg py-2 text-center text-white text-sm flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </div>
                <div className="flex-1 bg-blue-600 rounded-lg py-2 text-center text-white text-sm">
                  Text
                </div>
                <div className="flex-1 bg-pink-600 rounded-lg py-2 text-center text-white text-sm">
                  Instagram
                </div>
              </div>
            </div>
          </div>
          <div className="text-center">
            <p className="text-stone-400">
              Find your unique link in the <span className="text-amber-400 font-medium">Referrals</span> tab 
              of your dashboard. One click to copy!
            </p>
          </div>
        </div>
      )
    },
    {
      id: "step2",
      title: "Step 2: Share With Everyone",
      subtitle: "Multiple ways to spread the word",
      bg: "from-green-500/20 via-stone-900 to-stone-900",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
            {/* Share Method Cards */}
            {[
              { icon: MessageCircle, name: "WhatsApp", color: "bg-green-500", desc: "Send to contacts" },
              { icon: Users, name: "Text/SMS", color: "bg-blue-500", desc: "Direct message" },
              { icon: Share2, name: "Social Media", color: "bg-pink-500", desc: "Post your link" },
              { icon: Copy, name: "Copy & Paste", color: "bg-amber-500", desc: "Share anywhere" }
            ].map((method, idx) => (
              <div 
                key={method.name}
                className="bg-stone-800 rounded-xl p-4 border border-stone-700 text-center hover:border-stone-600 transition-all"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className={`w-12 h-12 ${method.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                  <method.icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-white font-medium">{method.name}</p>
                <p className="text-stone-500 text-xs">{method.desc}</p>
              </div>
            ))}
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 max-w-lg mx-auto">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-blue-400 flex-shrink-0" />
              <p className="text-stone-300 text-sm">
                <span className="text-white font-medium">Pro Tip:</span> Share to your Instagram story 
                with a &quot;Link in bio&quot; mention for maximum reach!
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "step3",
      title: "Step 3: Friends Sign Up",
      subtitle: "They click your link and register",
      bg: "from-purple-500/20 via-stone-900 to-stone-900",
      content: (
        <div className="space-y-6">
          {/* Visual Flow */}
          <div className="flex items-center justify-center gap-4 max-w-lg mx-auto">
            {/* Friend sees link */}
            <div className="bg-stone-800 rounded-xl p-4 border border-stone-700 text-center flex-1">
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <p className="text-white text-sm font-medium">Friend clicks</p>
              <p className="text-stone-500 text-xs">your link</p>
            </div>
            
            <ArrowRight className="w-6 h-6 text-stone-600" />
            
            {/* Registration */}
            <div className="bg-stone-800 rounded-xl p-4 border border-stone-700 text-center flex-1">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <p className="text-white text-sm font-medium">They register</p>
              <p className="text-stone-500 text-xs">free account</p>
            </div>
            
            <ArrowRight className="w-6 h-6 text-stone-600" />
            
            {/* You earn */}
            <div className="bg-stone-800 rounded-xl p-4 border border-amber-500/50 text-center flex-1">
              <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <Gift className="w-6 h-6 text-amber-400" />
              </div>
              <p className="text-white text-sm font-medium">You earn</p>
              <p className="text-amber-400 text-xs font-bold">+100 points!</p>
            </div>
          </div>
          
          {/* Mock notification */}
          <div className="bg-stone-800 rounded-xl p-4 border border-green-500/50 max-w-sm mx-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">New Referral!</p>
                <p className="text-stone-400 text-sm">Sarah M. just signed up using your link</p>
              </div>
              <Badge className="bg-amber-500 text-black ml-auto">+100</Badge>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "step4",
      title: "Step 4: Climb the Tiers",
      subtitle: "More referrals = better rewards",
      bg: "from-yellow-500/20 via-stone-900 to-stone-900",
      content: (
        <div className="space-y-6">
          {/* Tier Progression */}
          <div className="max-w-lg mx-auto space-y-3">
            {[
              { name: "Starter", icon: "👑", refs: 0, reward: "100 pts/referral", active: true },
              { name: "Supporter", icon: "🌟", refs: 5, reward: "150 pts + Badge", active: false },
              { name: "Advocate", icon: "⭐", refs: 10, reward: "200 pts + 500 bonus", active: false },
              { name: "Champion", icon: "🥇", refs: 25, reward: "1 month Pro FREE", active: false },
              { name: "Ambassador", icon: "🏆", refs: 50, reward: "VIP Status + Pro", active: false, featured: true }
            ].map((tier, idx) => (
              <div 
                key={tier.name}
                className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                  tier.featured 
                    ? "bg-amber-500/20 border-amber-500/50" 
                    : tier.active 
                      ? "bg-stone-800 border-green-500/50"
                      : "bg-stone-800/50 border-stone-700"
                }`}
              >
                <div className="text-2xl">{tier.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{tier.name}</span>
                    {tier.active && <Badge className="bg-green-500 text-white text-xs">YOU</Badge>}
                    {tier.featured && <Badge className="bg-amber-500 text-black text-xs">GOAL</Badge>}
                  </div>
                  <span className="text-stone-500 text-sm">{tier.refs} referrals</span>
                </div>
                <span className={`text-sm ${tier.featured ? "text-amber-400" : "text-stone-400"}`}>
                  {tier.reward}
                </span>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "step5",
      title: "Step 5: Win Big Prizes!",
      subtitle: "Ambassador Beta Promotion",
      bg: "from-amber-500/30 via-stone-900 to-stone-900",
      content: (
        <div className="space-y-6 text-center">
          {/* Prize showcase */}
          <div className="relative">
            <div className="w-32 h-32 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-amber-500/40 animate-pulse">
              <Trophy className="w-16 h-16 text-black" />
            </div>
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-bounce">
              LIMITED!
            </div>
          </div>
          
          <div>
            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold px-4 py-1 text-lg mb-4">
              🎉 BETA PROMO
            </Badge>
            <h3 className="text-2xl font-bold text-white mb-2">
              First 10 Ambassadors Win!
            </h3>
            <p className="text-stone-400 max-w-md mx-auto">
              Be one of the first 10 users to reach 50 referrals and get 
              <span className="text-amber-400 font-bold"> 1 MONTH FREE </span> 
              Pro Membership!
            </p>
          </div>

          {/* Progress mockup */}
          <div className="bg-stone-800 rounded-xl p-4 max-w-sm mx-auto border border-amber-500/30">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-stone-400">Your Progress</span>
              <span className="text-amber-400 font-bold">5 / 50</span>
            </div>
            <Progress value={10} className="h-3 bg-stone-700" />
            <p className="text-stone-500 text-xs mt-2">45 more to become an Ambassador!</p>
          </div>

          <div className="flex justify-center gap-2">
            <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
              Only 8 spots left!
            </Badge>
          </div>
        </div>
      )
    },
    {
      id: "getstarted",
      title: "Ready to Start Earning?",
      subtitle: "Your referral journey begins now",
      bg: "from-green-500/20 via-stone-900 to-stone-900",
      content: (
        <div className="text-center space-y-8">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-green-500/30">
            <Zap className="w-12 h-12 text-white" />
          </div>
          
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">Let&apos;s Go!</h3>
            <p className="text-stone-400 max-w-md mx-auto">
              Head to your Referral Dashboard, copy your unique link, 
              and start sharing. Every sign-up counts!
            </p>
          </div>

          <div className="flex flex-col gap-3 max-w-xs mx-auto">
            <Button 
              onClick={onComplete}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold py-6"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Go to My Dashboard
            </Button>
            <Button 
              variant="outline" 
              onClick={onComplete}
              className="border-stone-600 text-stone-300 hover:text-white hover:bg-stone-800"
            >
              I&apos;ll explore later
            </Button>
          </div>
        </div>
      )
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || hasInteracted) return;
    
    const timer = setInterval(() => {
      setCurrentSlide(prev => {
        if (prev >= slides.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 5000); // 5 seconds per slide

    return () => clearInterval(timer);
  }, [isPlaying, hasInteracted, slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setHasInteracted(true);
    setIsPlaying(false);
  }, []);

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
      setHasInteracted(true);
    }
  }, [currentSlide, slides.length]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
      setHasInteracted(true);
    }
  }, [currentSlide]);

  const toggleAutoPlay = useCallback(() => {
    setIsPlaying(prev => !prev);
    setHasInteracted(false);
  }, []);

  const slide = slides[currentSlide];

  return (
    <div className={`relative bg-gradient-to-br ${slide.bg} rounded-2xl overflow-hidden`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-stone-700/50">
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
            <Trophy className="w-3 h-3 mr-1" />
            Referral Program
          </Badge>
          <span className="text-stone-500 text-sm">
            {currentSlide + 1} / {slides.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleAutoPlay}
            className="text-stone-400 hover:text-white"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span className="ml-1 text-xs">{isPlaying ? "Pause" : "Auto"}</span>
          </Button>
          {showCloseButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onComplete}
              className="text-stone-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar for auto-play */}
      {isPlaying && (
        <div className="h-1 bg-stone-700">
          <div 
            className="h-full bg-amber-500 transition-all duration-[5000ms] ease-linear"
            style={{ width: "100%" }}
            key={currentSlide}
          />
        </div>
      )}

      {/* Slide Content */}
      <div className="p-6 md:p-8 min-h-[400px] flex flex-col">
        {/* Title */}
        <div className="text-center mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-1">{slide.title}</h2>
          <p className="text-stone-400">{slide.subtitle}</p>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center">
          {slide.content}
        </div>
      </div>

      {/* Navigation */}
      <div className="p-4 border-t border-stone-700/50">
        <div className="flex items-center justify-between">
          {/* Previous */}
          <Button
            variant="ghost"
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="text-stone-400 hover:text-white disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>

          {/* Dots */}
          <div className="flex items-center gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentSlide 
                    ? "w-6 bg-amber-500" 
                    : "bg-stone-600 hover:bg-stone-500"
                }`}
              />
            ))}
          </div>

          {/* Next */}
          {currentSlide < slides.length - 1 ? (
            <Button
              onClick={nextSlide}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={onComplete}
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralOnboarding;
